import {
  Amenity,
  type AmenityStatus,
  MealPlanKind,
} from "@b2bportal/lodging-api";

const FREE_BREAKFAST_AMENITIES: ReadonlySet<string> = new Set([
  Amenity.FreeBreakfast,
  Amenity.BreakfastIncluded,
]);

const BREAKFAST_MEAL_PLAN_KINDS: ReadonlySet<string> = new Set([
  MealPlanKind.Breakfast,
  MealPlanKind.BreakfastFor1,
  MealPlanKind.BreakfastFor2,
]);

const getMealPlanDisplayName = (
  kind: MealPlanKind,
  t: (key: string) => string
): string => {
  switch (kind) {
    case MealPlanKind.Breakfast:
      return t("core-hotels.mealPlan.Breakfast");
    case MealPlanKind.BreakfastFor1:
      return t("core-hotels.mealPlan.BreakfastFor1");
    case MealPlanKind.BreakfastFor2:
      return t("core-hotels.mealPlan.BreakfastFor2");
    case MealPlanKind.HalfBoard:
      return t("core-hotels.mealPlan.HalfBoard");
    case MealPlanKind.FullBoard:
      return t("core-hotels.mealPlan.FullBoard");
    case MealPlanKind.AllInclusive:
      return t("core-hotels.mealPlan.AllInclusive");
    case MealPlanKind.Lunch:
      return t("core-hotels.mealPlan.Lunch");
    case MealPlanKind.Dinner:
      return t("core-hotels.mealPlan.Dinner");
    default:
      return t("core-hotels.mealPlan.RoomOnly");
  }
};

/**
 * Computes effective amenities by replacing or adding meal plan information.
 *
 * Priority:
 * 1. Common meal plan across all rooms (breakfast plans add FreeBreakfast only when
 *    no existing breakfast amenity is present — PaidBreakfast is never overridden
 *    because MealPlanKind lacks a paid/free distinction; non-breakfast plans like
 *    AllInclusive replace breakfast amenity with Restaurant + meal plan name)
 * 2. Free breakfast detection via room amenities
 * 3. Base amenities unchanged
 */
export const getEffectiveAmenities = ({
  amenities,
  hotelMealPlanKind,
  allRoomsHaveFreeBreakfast,
  roomsLoaded = true,
  t,
}: {
  amenities: AmenityStatus[];
  hotelMealPlanKind: MealPlanKind | undefined;
  allRoomsHaveFreeBreakfast: boolean;
  roomsLoaded?: boolean;
  t: (key: string) => string;
}): AmenityStatus[] => {
  if (!roomsLoaded) {
    return amenities.filter((a) => a.amenity !== Amenity.PaidBreakfast);
  }

  if (hotelMealPlanKind !== undefined) {
    const isBreakfastPlan = BREAKFAST_MEAL_PLAN_KINDS.has(hotelMealPlanKind);

    if (isBreakfastPlan) {
      if (amenities.some((a) => FREE_BREAKFAST_AMENITIES.has(a.amenity))) {
        return amenities;
      }

      const displayName = t(
        "core-hotels.availabilityPage.results.hotelCard.features.breakfastFree"
      );

      if (amenities.some((a) => a.amenity === Amenity.PaidBreakfast)) {
        return amenities;
      }

      return [{ amenity: Amenity.FreeBreakfast, displayName }, ...amenities];
    }

    // Non-breakfast meal plan (AllInclusive, FullBoard, etc.)
    const displayName = getMealPlanDisplayName(hotelMealPlanKind, t);
    const mealPlanEntry: AmenityStatus = {
      amenity: Amenity.Restaurant,
      displayName,
    };

    const hasAnyBreakfastAmenity = amenities.some(
      (a) =>
        FREE_BREAKFAST_AMENITIES.has(a.amenity) ||
        a.amenity === Amenity.PaidBreakfast
    );

    if (hasAnyBreakfastAmenity) {
      return amenities.map((a) =>
        FREE_BREAKFAST_AMENITIES.has(a.amenity) ||
        a.amenity === Amenity.PaidBreakfast
          ? mealPlanEntry
          : a
      );
    }

    if (amenities.some((a) => a.amenity === Amenity.Restaurant)) {
      return amenities;
    }
    return [mealPlanEntry, ...amenities];
  }

  if (!allRoomsHaveFreeBreakfast) {
    return amenities;
  }

  if (amenities.some((a) => FREE_BREAKFAST_AMENITIES.has(a.amenity))) {
    return amenities;
  }

  const freeBreakfastDisplayName = t(
    "core-hotels.availabilityPage.results.hotelCard.features.breakfastFree"
  );

  if (amenities.some((a) => a.amenity === Amenity.PaidBreakfast)) {
    return amenities;
  }

  return [
    { amenity: Amenity.FreeBreakfast, displayName: freeBreakfastDisplayName },
    ...amenities,
  ];
};
